import { Session, SESSION_STATUS_SHORT_ERROR_TEXT, SessionErrorDetails, SessionErrorKey, SessionStatus } from '../models/session.model';
import { CustomUtils } from './custom-utils';

export class SessionErrorDetailsUtils {
  static TELQ_IP = '52.29.63.229';

  static COMMAND_STATUS_NAMES = {
    0x00000005: 'ESME_RALYBND',
    0x0000000d: 'ESME_RBINDFAIL',
    0x0000000e: 'ESME_RINVPASWD',
    0x0000000f: 'ESME_RINVSYSID',
  };

  static toShortText(errorKey: SessionErrorKey | null): string {
    return errorKey ? SESSION_STATUS_SHORT_ERROR_TEXT[errorKey] || 'Unknown error' : 'Unknown error';
  }

  static toCommandStatusHex(commandStatus: number | null | undefined) {
    if (commandStatus === null || commandStatus === undefined) {
      return null;
    }
    return `0x${Number(commandStatus).toString(16).toUpperCase().padStart(8, '0')}`;
  }

  static toCommandStatusName(commandStatus: number | null | undefined) {
    if (commandStatus === null || commandStatus === undefined) {
      return null;
    }
    return this.COMMAND_STATUS_NAMES[Number(commandStatus) as keyof typeof this.COMMAND_STATUS_NAMES] ?? null;
  }

  static resolveDescription(errorKey: SessionErrorKey | null, commandStatus: number | null | undefined) {
    switch (errorKey) {
      case SessionErrorKey.TCP_CONNECT_TIMEOUT:
        return '';

      case SessionErrorKey.TCP_CHANNEL_UNEXPECTEDLY_CLOSED:
        return 'The remote server closed the TCP connection.';

      case SessionErrorKey.TCP_CHANNEL_CLOSED_AFTER_REQUEST_SENT:
        return 'Connection was lost between request and response.';

      case SessionErrorKey.SMPP_BIND_ALREADY_BOUND:
        return 'A previous session is still active on the remote server.';

      case SessionErrorKey.SMPP_BIND_INVALID_CREDENTIALS:
        if (commandStatus === 0x0000000e) {
          return 'Invalid password.';
        }
        if (commandStatus === 0x0000000f) {
          return 'Invalid system ID.';
        }
        return 'Invalid login data.';

      case SessionErrorKey.SMPP_BIND_FAILED:
        return 'bind_transceiver_resp was not received or returned an error.';

      case SessionErrorKey.SMPP_SESSION_NO_RESPONSE:
        return 'A request was sent but no response was received within the timeout.';

      case SessionErrorKey.SMPP_SESSION_UNBIND_REQUEST_RECEIVED:
        return 'The remote server sent an unbind request.';

      case SessionErrorKey.SESSION_COLLECTOR_SHUTDOWN_DUPLICATE_SESSION:
        return 'A duplicate session was detected. The session collector is performing a cleanup or shutdown.';

      case SessionErrorKey.SESSION_COLLECTOR_SHUTDOWN_REQUESTED:
      case SessionErrorKey.SESSION_COLLECTOR_CLEANUP_STOPPING:
        return 'The session collector is performing a cleanup or shutdown.';

      case SessionErrorKey.SMPP_SESSION_UNKNOWN_THROWABLE:
      case SessionErrorKey.SMPP_UNKNOWN_ERROR:
      default:
        return 'An unexpected internal error occurred, please contact support.';
    }
  }

  static resolveGuidance(errorKey: SessionErrorKey | null, commandStatus: number | null | undefined) {
    switch (errorKey) {
      case SessionErrorKey.TCP_CONNECT_TIMEOUT:
        return `Connection timed out. Verify host and port are reachable and that TelQ's IP (${this.TELQ_IP}) is whitelisted in your firewall.`;

      case SessionErrorKey.TCP_CHANNEL_UNEXPECTEDLY_CLOSED:
        return 'The remote server closed the TCP connection. Check server logs for crashes or restarts.';

      case SessionErrorKey.TCP_CHANNEL_CLOSED_AFTER_REQUEST_SENT:
        return 'Connection lost between request and response. May indicate unstable network or server overload.';

      case SessionErrorKey.SMPP_BIND_INVALID_CREDENTIALS:
        if (commandStatus === 0x0000000e || commandStatus === 0x0000000f) {
          return 'Credentials are incorrect. Verify login (system ID) and password.';
        }
        return 'Credentials are incorrect. Verify login (system ID) and password.';

      case SessionErrorKey.SMPP_BIND_ALREADY_BOUND:
        return 'A previous session is still active on the remote server. TelQ will automatically reconnect once it expires. No action needed unless the issue persists.';

      case SessionErrorKey.SMPP_BIND_FAILED:
        return `bind_transceiver_resp not received or returned error. This is a catch-all for any bind error not listed above. Check your server accepts transceiver binds and that TelQ's IP (${this.TELQ_IP}) is whitelisted.`;

      case SessionErrorKey.SMPP_SESSION_NO_RESPONSE:
        return 'Our server sent a request (enquire_link or submit_sm) but received no response within the timeout. This can also occur during bind if the remote server does not respond. Check your server is processing SMPP requests and responding in time.';

      case SessionErrorKey.SMPP_SESSION_UNBIND_REQUEST_RECEIVED:
        return 'Your server sent an unbind request. Check your session management, keepalive configuration, or idle timeout settings.';

      case SessionErrorKey.SESSION_COLLECTOR_SHUTDOWN_DUPLICATE_SESSION:
        return 'A duplicate session was detected. The session collector is performing a cleanup or shutdown. If this persists, contact TelQ support.';

      case SessionErrorKey.SMPP_SESSION_UNKNOWN_THROWABLE:
      case SessionErrorKey.SMPP_UNKNOWN_ERROR:
      case SessionErrorKey.SESSION_COLLECTOR_SHUTDOWN_REQUESTED:
      case SessionErrorKey.SESSION_COLLECTOR_CLEANUP_STOPPING:
      default:
        return 'An unexpected internal error occurred. If this persists, contact TelQ support with the timestamp of the error.';
    }
  }

  static buildErrorDetails(session: Session): SessionErrorDetails {
    const errorKey = session.errorKey ?? null;
    const commandStatus = session.commandStatus ?? null;
    const errorLayer = session.errorLayer ?? null;

    return {
      hostIp: session.hostIp ?? '',
      statusCode: session.statusCode ?? null,
      errorLayer,
      flowStage: session.flowStage ?? null,
      errorKey,
      shortText: this.toShortText(errorKey),
      commandStatusHex: this.toCommandStatusHex(commandStatus),
      commandStatusName: this.toCommandStatusName(commandStatus),
      description: this.resolveDescription(errorKey, commandStatus),
      guidance: this.resolveGuidance(errorKey, commandStatus),
      lastError: session.lastError ?? '',
      lastErrorAt: session.lastErrorAt ? new Date(session.lastErrorAt) : null,
      lastErrorAtFormatted: session.lastErrorAt ? CustomUtils.formatRelativeDate(new Date(session.lastErrorAt)) : null,
      lastErrorAtAbsoluteFormatted: session.lastErrorAt ? CustomUtils.formatDate(new Date(session.lastErrorAt)).join(' ') : null,
      failedTimes: session.failedTimes ?? 0,
      isReconnecting: session.statusCode === SessionStatus.RECONNECTING,
      isConnecting: session.lastError === 'Initial Connection...',
      isAutoRetrying: Boolean(errorKey && errorKey !== SessionErrorKey.SMPP_BIND_INVALID_CREDENTIALS),
    };
  }
}
